import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined'
import Modal from '../common/Modal'
import clickableRowProps from '../../helpers/a11y/clickableRowProps';

/**
 * @typedef {object} StaticCardOption
 * @property {string} type - Static card type key from the registry
 * @property {string} label - Human-readable card name
 * @property {boolean} isActive - True when the card is currently on the dashboard
 * @property {string | null} existingDocId - Soft-deleted DashboardItems doc to restore, if any
 * @property {{ variant: string, label: string } | null} pill - Category pill matching the card's own header, if any
 */

/**
 * Modal behind the dashboard "Aanpassen" button. Lists every static card
 * available to this tenant — active ones marked as added, the rest addable —
 * plus a chooser option that navigates to the analysis page for
 * visualisation cards.
 *
 * @param {object} props
 * @param {boolean} props.modalOpen - Whether the modal is shown
 * @param {(open: boolean) => void} props.setModalOpen - Toggles the modal
 * @param {Array<StaticCardOption>} props.staticCards - All static cards available to this tenant
 * @param {(card: StaticCardOption) => void} props.restoreCard - Adds/restores a static card
 * @param {boolean} props.showAnalysisLink - Whether the analysis-cards option is shown
 * @param {() => void} props.onGoToAnalysis - Navigates to the analysis page
 */
const DashboardCustomizeModal = ({
    modalOpen,
    setModalOpen,
    staticCards,
    restoreCard,
    showAnalysisLink,
    onGoToAnalysis,
}) => {
    const content = (
        <div className='dashboard-customize-modal'>
            <h3 className='dashboard-customize-modal-section-title'>Vaste kaarten toevoegen</h3>
            {staticCards.length === 0 ? (
                <p className='dashboard-restore-modal-empty'>Geen vaste kaarten beschikbaar.</p>
            ) : (
                <ul className='new-dashboard-hidden-cards-list'>
                    {staticCards.map((card) => (
                        <li key={card.type} className='new-dashboard-hidden-cards-row'>
                            <span className='new-dashboard-hidden-cards-label'>{card.label}</span>
                            {card.pill && (
                                <span className={`new-dashboard-card-label-type new-dashboard-card-label-type-${card.pill.variant}`}>
                                    {card.pill.label}
                                </span>
                            )}
                            {card.isActive ? (
                                <span className='new-dashboard-hidden-cards-added'>
                                    <CheckOutlinedIcon />
                                    <span>Toegevoegd</span>
                                </span>
                            ) : (
                                <button
                                    type='button'
                                    className='new-dashboard-hidden-cards-restore'
                                    onClick={() => restoreCard(card)}
                                >
                                    <AddOutlinedIcon />
                                    <span>Toevoegen</span>
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {showAnalysisLink && (
                <div className='options-modal-list'>
                    <div
                        className='options-modal-item' {...clickableRowProps(() => {
                            setModalOpen(false)
                            onGoToAnalysis()
                        })}
                    >
                        <div className='options-modal-item__icon options-modal-item__icon--portfolio'>
                            <QueryStatsOutlinedIcon />
                        </div>
                        <div className='options-modal-item__body'>
                            <p className='options-modal-item__title'>Analyse kaarten toevoegen</p>
                            <p className='options-modal-item__description'>
                                Kies een grafiek of meting op de analysepagina en pin deze op het dashboard.
                            </p>
                        </div>
                        <ChevronRightIcon className='options-modal-item__chevron' />
                    </div>
                </div>
            )}
        </div>
    )

    return (
        <Modal
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            hideConfirm
            cancelLabel='Sluiten'
            pageHeader='Dashboard aanpassen'
            content={content}
        />
    )
}

export default DashboardCustomizeModal
